// src/screens/superadmin/orders/Orders.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import {
  FaSearch,
  FaShoppingCart,
  FaCheckCircle,
  FaTruck,
  FaTimes,
  FaBox,
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaDollarSign,
  FaClock,
  FaTag,
  FaStore,
  FaUndo,
  FaUniversity,
  FaImages,
} from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import Button from "../../../components/general/button/Button";
import Pagination from "../../../components/general/pagination/Pagination";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import StatCard from "../../../components/admin/statcard/StatCard";
import "../../../assets/css/admin/orders/Orders.css";
import AdminLayout from "../../../layouts/AdminLayout";
import MyContext from "../../../context/MyContext";
import { OrdersListSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const OrdersList = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [activeTab, setActiveTab] = useState("all");

  const [currentPage, setCurrentPage]   = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [searchTerm, setSearchTerm]       = useState("");
  const [filterStatus, setFilterStatus]   = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");

  const [loading, setLoading]       = useState(true);
  const [ordersList, setOrdersList] = useState([]);

  // ── NEW: stats ab controller se aate hain (CustomerOrderController@viewAllOrders jaisa pattern) ──
  const [totalOrders, setTotalOrders]         = useState(0);
  const [pendingOrders, setPendingOrders]     = useState(0);
  const [shippedOrders, setShippedOrders]     = useState(0);
  const [deliveredOrders, setDeliveredOrders] = useState(0);

  // ── NEW: Bank Transfer Verification (Module 3) ──
  const [verifyingOrderId, setVerifyingOrderId] = useState(null);

  // ── NEW: Image-Verification Checkpoints — timeline modal ──
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineData, setTimelineData]           = useState([]);
  const [loadingTimeline, setLoadingTimeline]     = useState(false);
  const [timelineOrderId, setTimelineOrderId]     = useState(null);

  // ── FIXED: sirf Online payment hai, Cash on Delivery hata di ──
  const paymentOptions = [
    { value: "all",    label: "All Payments" },
    { value: "online", label: "Online"       },
  ];

  const viewAllOrders = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/order/viewAllOrders`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else {
          setOrdersList(response.data.orders);
          setTotalOrders(response.data.totalOrders);
          setPendingOrders(response.data.pendingOrders);
          setShippedOrders(response.data.shippedOrders);
          setDeliveredOrders(response.data.deliveredOrders);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load orders." });
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllOrders();
  }, [authToken]);

  const markAsRefunded = (order) => {
    Swal.fire({
      title: "Confirm Refund Sent?",
      html: `
        <p style="font-size:13px;text-align:left;">
          Bank: <strong>${order.refund_bank_name || "N/A"}</strong><br/>
          Account Title: <strong>${order.refund_account_title || "N/A"}</strong><br/>
          Account Number: <strong>${order.refund_account_number || "N/A"}</strong><br/>
          Amount: <strong>PKR ${Number(order.subTotal || 0).toLocaleString()}</strong>
        </p>
        <p style="font-size:13px;text-align:left;margin-top:10px;">
          Whose fault was this? (their earning will be deducted by the refund amount)
        </p>
        <select id="admin-refund-fault" class="swal2-select" style="width:100%;">
          <option value="none">None / Customer's fault (no deduction)</option>
          <option value="seller">Seller</option>
          <option value="deliveryman">Deliveryman</option>
        </select>
        <p style="font-size:13px;text-align:left;margin-top:10px;">
          Upload the bank transfer receipt as proof of payment:
        </p>
        <input id="admin-refund-receipt" type="file" accept="image/*" class="swal2-file">
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#097d43",
      confirmButtonText: "Yes, I've sent the transfer",
      preConfirm: () => {
        const fileInput = document.getElementById("admin-refund-receipt");
        const faultSelect = document.getElementById("admin-refund-fault");
        const file = fileInput?.files?.[0];
        if (!file) {
          Swal.showValidationMessage("Please upload the transfer receipt");
          return false;
        }
        return { file, faultParty: faultSelect?.value || "none" };
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      const formData = new FormData();
      formData.append("id", order.id);
      formData.append("receiptImage", result.value.file);
      formData.append("fault_party", result.value.faultParty);

      axios
        .post(`${baseUrl}/admin/refund/markAsRefunded`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        })
        .then((response) => {
          if (response.data.success) {
            Swal.fire({ icon: "success", title: "Success", text: "Refund marked as completed!" });
            viewAllOrders();
          } else {
            Swal.fire({ icon: "error", title: "Error", text: response.data.message });
          }
        })
        .catch(() =>
          Swal.fire({ icon: "error", title: "Error", text: "Failed to update refund status." })
        );
    });
  };

  // ══════════════════════════════════════════════════════
  // NEW — Bank Transfer verification. Admin dekhta hai customer ne
  // kitna transfer kiya (transferred_amount) vs order ka total
  // (totalPrice), receipt dekh ke ek click se verify kar deta hai —
  // backend (verifyBankTransfer) khud decide karta hai match/under/
  // over-payment case, koi alag reject button nahi chahiye.
  // ══════════════════════════════════════════════════════
  const verifyBankTransfer = (order) => {
    const transferred = Number(order.transferred_amount || 0);
    const total = Number(order.totalPrice || 0);
    const diff = transferred - total;

    let diffLine = `<span style="color:#097d43;">Amount matches order total.</span>`;
    if (diff < 0) {
      diffLine = `<span style="color:#dc3545;">Underpaid by PKR ${Math.abs(diff).toLocaleString()} — verifying will cancel this order and the customer will be asked to raise a refund ticket for the transferred amount.</span>`;
    } else if (diff > 0) {
      diffLine = `<span style="color:#b8860b;">Overpaid by PKR ${diff.toLocaleString()} — verifying will confirm the order and the customer will be notified they can raise a ticket for the excess amount.</span>`;
    }

    Swal.fire({
      title: `Verify Payment — Order #${order.id}`,
      html: `
        <div style="text-align:left;font-size:13px;">
          <p>Order Total: <strong>PKR ${total.toLocaleString()}</strong></p>
          <p>Transferred Amount: <strong>PKR ${transferred.toLocaleString()}</strong></p>
          <p style="margin-top:8px;">${diffLine}</p>
        </div>
      `,
      imageUrl: order.transfer_receipt_image ? `${baseUrlForFiles}/${order.transfer_receipt_image}` : undefined,
      imageWidth: 320,
      imageAlt: "Transfer receipt",
      showCancelButton: true,
      confirmButtonColor: "#097d43",
      confirmButtonText: "Verify Payment",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setVerifyingOrderId(order.id);
      const formData = new FormData();
      formData.append("order_id", order.id);

      axios
        .post(`${baseUrl}/admin/order/verifyBankTransfer`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((response) => {
          setVerifyingOrderId(null);
          if (response.data.success) {
            Swal.fire({ icon: "success", title: "Done", text: response.data.message });
            viewAllOrders();
          } else {
            Swal.fire({ icon: "error", title: "Error", text: response.data.message });
          }
        })
        .catch(() => {
          setVerifyingOrderId(null);
          Swal.fire({ icon: "error", title: "Error", text: "Failed to verify payment." });
        });
    });
  };

  // ══════════════════════════════════════════════════════
  // NEW — Image-Verification Checkpoints timeline. Sirf jitne
  // checkpoints ki image maujood hai wahi backend se aate hain.
  // ══════════════════════════════════════════════════════
  const viewImageTimeline = (order) => {
    setTimelineOrderId(order.id);
    setShowTimelineModal(true);
    setLoadingTimeline(true);
    setTimelineData([]);

    const formData = new FormData();
    formData.append("order_id", order.id);

    axios
      .post(`${baseUrl}/admin/order/viewImageTimeline`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoadingTimeline(false);
        if (response.data.success) {
          setTimelineData(response.data.timeline || []);
        } else {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message });
        }
      })
      .catch(() => {
        setLoadingTimeline(false);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load image timeline." });
      });
  };

  // ── refund tracking do alag tabs mein split hai: "Refund Requests"
  // sirf action-needed (pending) dikhata hai, "Refunded" alag tab mein
  // complete ho chuki refunds ki history hai ──
  const refundPendingOrders = ordersList.filter(o => o.paymentStatus === "Refund Pending");
  const refundedOrders = ordersList.filter(o => o.paymentStatus === "Refunded");

  // ── NEW: bank-transfer receipts jo admin ke review ka intezar kar rahe hain ──
  const bankTransferPendingOrders = ordersList.filter(
    (o) => o.paymentMethod === "bank_transfer" && o.paymentStatus === "Pending Verification"
  );

  // ── FIXED: ab controller se aayi values use ho rahi hain ──
  const statsData = [
    { icon: FaShoppingCart, iconBg: "linear-gradient(135deg,#e3f2fd,#bbdefb)", iconColor: "#2196f3", label: "Total Orders",     value: totalOrders.toString(),     badge: "All"        },
    { icon: FaClock,        iconBg: "linear-gradient(135deg,#fff3e0,#ffe0b2)", iconColor: "#ff9800", label: "Pending Orders",   value: pendingOrders.toString(),   badge: "Processing" },
    { icon: FaTruck,        iconBg: "linear-gradient(135deg,#e8f5f0,#d4ede3)", iconColor: "#0ac46c", label: "Shipped Orders",   value: shippedOrders.toString(),   badge: "In Transit" },
    { icon: FaCheckCircle,  iconBg: "linear-gradient(135deg,#f3e5f5,#e1bee7)", iconColor: "#9c27b0", label: "Delivered Orders", value: deliveredOrders.toString(), badge: "Completed"  },
  ];

  const filteredOrders = ordersList.filter((o) => {
    if (activeTab === "refunds") {
      return o.paymentStatus === "Refund Pending";
    }

    if (activeTab === "refunded") {
      return o.paymentStatus === "Refunded";
    }

    if (activeTab === "bankTransfer") {
      return o.paymentMethod === "bank_transfer" && o.paymentStatus === "Pending Verification";
    }

    const ms =
      o.id.toString().includes(searchTerm) ||
      o.kmcustomer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.kmseller?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const mf = filterStatus  === "all" || o.status      === filterStatus;
    const mp = filterPayment === "all" || o.paymentType === filterPayment;
    return ms && mf && mp;
  });

  const getActions = (order) => {
    const actions = [
      {
        label:   "View Details",
        onClick: () => {
          setSelectedOrder(order);
          setShowViewModal(true);
        },
      },
      // ── NEW: har order pe timeline available hai — sirf wahi
      // checkpoints dikhenge jinki image actually maujood hai ──
      {
        label:   "View Image Timeline",
        onClick: () => viewImageTimeline(order),
      },
    ];

    if (order.paymentMethod === "bank_transfer" && order.paymentStatus === "Pending Verification") {
      actions.push({
        label: "Verify Payment",
        onClick: () => verifyBankTransfer(order),
        variant: "success",
      });
    }

    if (order.paymentStatus === "Refund Pending") {
      actions.push({
        label: "Mark as Refunded",
        onClick: () => markAsRefunded(order),
        variant: "success",
      });
    }

    if (order.paymentStatus === "Refunded" && order.refund_receipt_image) {
      actions.push({
        label: "View Receipt",
        onClick: () => {
          Swal.fire({
            title: `Refund Receipt — Order #${order.id}`,
            imageUrl: `${baseUrlForFiles}/${order.refund_receipt_image}`,
            imageAlt: "Refund receipt",
            imageWidth: 400,
            confirmButtonColor: "#097d43",
          });
        },
      });
    }

    return actions;
  };

  // ── ksa-order-badge-* classes CSS mein colors define nahi kar
  // rahi thi, is liye inline colors laga rahe hain (customer
  // Orders.js mein bhi yahi fix use hua hai) ──
  const orderStatusColors = {
    "Pending":    { color: "#b8860b", bgColor: "#fff8e1" },
    "Processing": { color: "#097d43", bgColor: "#e8f5f0" },
    "Shipping":   { color: "#0d6efd", bgColor: "#e7f1ff" },
    "Shipped":    { color: "#0d6efd", bgColor: "#e7f1ff" },
    "Delivered":  { color: "#28a745", bgColor: "#e8f5e9" },
    "Cancelled":  { color: "#dc3545", bgColor: "#ffebee" },
  };

  const getStatusLabel = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

  const StatusBadge = ({ status }) => {
    const cfg = orderStatusColors[status] || { color: "#6c757d", bgColor: "#f1f1f1" };
    return (
      <span
        className="ksa-order-status-badge"
        style={{
          color: cfg.color,
          backgroundColor: cfg.bgColor,
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 12px",
          borderRadius: "50px",
          fontWeight: 600,
          fontSize: "13px",
        }}
      >
        {getStatusLabel(status)}
      </span>
    );
  };

  // ── refund ka apna alag status hai (paymentStatus), order.status
  // se mix nahi karna — "Refund Pending" amber, "Refunded" green ──
  const RefundStatusBadge = ({ paymentStatus }) => {
    const isRefunded = paymentStatus === "Refunded";
    return (
      <span
        className="ksa-order-status-badge"
        style={{
          color: isRefunded ? "#28a745" : "#b8860b",
          backgroundColor: isRefunded ? "#e8f5e9" : "#fff8e1",
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 12px",
          borderRadius: "50px",
          fontWeight: 600,
          fontSize: "13px",
        }}
      >
        {paymentStatus}
      </span>
    );
  };

  // ── formatDate ────────────────────────────────────────────────
  // created_at?.slice(0, 10) raw ISO format (2026-08-06) de raha
  // tha — ab readable trim format mein show hoga.
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <OrdersListSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>

        <div className="ksa-order-page-header">
          <div>
            <h1 className="ksa-layout-page-title">Orders Management</h1>
            <p className="ksa-layout-page-subtitle">
              Monitor and manage all customer orders and deliveries.
            </p>
          </div>
        </div>

        <Row className="g-4 mb-4 ksa-order-stats">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} sm={6} xl={3}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        <div className="ksa-card">

          <div className="ksa-order-tabs">
            <button
              className={`ksa-order-tab ${activeTab === "all" ? "active" : ""}`}
              onClick={() => { setActiveTab("all"); setCurrentPage(1); }}
            >
              <FaShoppingCart /> All Orders
              <span className="ksa-tab-badge">{ordersList.length}</span>
            </button>
            <button
              className={`ksa-order-tab ${activeTab === "bankTransfer" ? "active" : ""}`}
              onClick={() => { setActiveTab("bankTransfer"); setCurrentPage(1); }}
            >
              <FaUniversity /> Bank Transfer Approval
              {bankTransferPendingOrders.length > 0 && (
                <span className="ksa-tab-badge ksa-tab-badge-warning">
                  {bankTransferPendingOrders.length}
                </span>
              )}
            </button>
            <button
              className={`ksa-order-tab ${activeTab === "refunds" ? "active" : ""}`}
              onClick={() => { setActiveTab("refunds"); setCurrentPage(1); }}
            >
              <FaUndo /> Refund Requests
              {refundPendingOrders.length > 0 && (
                <span className="ksa-tab-badge ksa-tab-badge-warning">
                  {refundPendingOrders.length}
                </span>
              )}
            </button>
            <button
              className={`ksa-order-tab ${activeTab === "refunded" ? "active" : ""}`}
              onClick={() => { setActiveTab("refunded"); setCurrentPage(1); }}
            >
              <FaCheckCircle /> Refunded
              <span className="ksa-tab-badge">{refundedOrders.length}</span>
            </button>
          </div>

          {activeTab === "all" && (
            <div className="ksa-order-table-header">
              <div className="ksa-order-filters">
                <select
                  className="ksa-order-filter-select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  {["Pending","Processing","Shipping","Shipped","Delivered","Cancelled"].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <select
                  className="ksa-order-filter-select"
                  value={filterPayment}
                  onChange={(e) => setFilterPayment(e.target.value)}
                >
                  {paymentOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="ksa-order-search">
                <FaSearch size={16} />
                <input
                  type="text"
                  placeholder="Search by order ID, customer, seller..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="ksa-order-table-wrapper">
            {activeTab === "bankTransfer" ? (
              <table className="ksa-order-table">
                <thead>
                  <tr>
                    <th>ORDER ID</th>
                    <th>CUSTOMER</th>
                    <th>ORDER TOTAL</th>
                    <th>TRANSFERRED</th>
                    <th>RECEIPT</th>
                    <th>DATE</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((order) => {
                      const transferred = Number(order.transferred_amount || 0);
                      const total = Number(order.totalPrice || 0);
                      const diff = transferred - total;

                      return (
                        <tr key={order.id}>
                          <td className="ksa-order-number">{order.id}</td>
                          <td>
                            <div className="ksa-order-customer">
                              <p className="ksa-order-customer-name">{order.kmcustomer?.name}</p>
                              <p className="ksa-order-customer-email">{order.kmcustomer?.email}</p>
                            </div>
                          </td>
                          <td className="ksa-order-total">PKR {total.toLocaleString()}</td>
                          <td
                            className="ksa-order-total"
                            style={{ color: diff < 0 ? "#dc3545" : diff > 0 ? "#b8860b" : "#28a745", fontWeight: 700 }}
                          >
                            PKR {transferred.toLocaleString()}
                          </td>
                          <td>
                            {order.transfer_receipt_image ? (
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  Swal.fire({
                                    title: `Receipt — Order #${order.id}`,
                                    imageUrl: `${baseUrlForFiles}/${order.transfer_receipt_image}`,
                                    imageAlt: "Transfer receipt",
                                    imageWidth: 400,
                                    confirmButtonColor: "#097d43",
                                  })
                                }
                              >
                                View
                              </Button>
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td className="ksa-order-date">{formatDate(order.updated_at)}</td>
                          <td>
                            <Button
                              variant="primary"
                              onClick={() => verifyBankTransfer(order)}
                              disabled={verifyingOrderId === order.id}
                            >
                              {verifyingOrderId === order.id ? "Verifying..." : "Verify Payment"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}

                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "20px" }}>
                        No bank transfer receipts awaiting approval.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (activeTab === "refunds" || activeTab === "refunded") ? (
              <table className="ksa-order-table">
                <thead>
                  <tr>
                    <th>ORDER ID</th>
                    <th>CUSTOMER</th>
                    <th>AMOUNT</th>
                    <th>REFUND TO (BANK DETAILS)</th>
                    <th>STATUS</th>
                    <th>DATE</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((order) => (
                      <tr key={order.id}>
                        <td className="ksa-order-number">{order.id}</td>
                        <td>
                          <div className="ksa-order-customer">
                            <p className="ksa-order-customer-name">{order.kmcustomer?.name}</p>
                            <p className="ksa-order-customer-email">{order.kmcustomer?.email}</p>
                          </div>
                        </td>
                        <td className="ksa-order-total">PKR {order.subTotal?.toLocaleString()}</td>
                        <td>
                          <div style={{ fontSize: 12.5 }}>
                            <p style={{ margin: 0 }}>{order.refund_bank_name || "N/A"}</p>
                            <p style={{ margin: 0, color: "#6c757d" }}>{order.refund_account_title || "N/A"}</p>
                            <p style={{ margin: 0, color: "#6c757d" }}>{order.refund_account_number || "N/A"}</p>
                          </div>
                        </td>
                        <td>
                          <RefundStatusBadge paymentStatus={order.paymentStatus} />
                        </td>
                        <td className="ksa-order-date">{formatDate(order.updated_at)}</td>
                        <td>
                          <ActionDropdown actions={getActions(order)} />
                        </td>
                      </tr>
                    ))}

                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "20px" }}>
                        {activeTab === "refunded" ? "No refunded orders yet." : "No refund requests."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="ksa-order-table">
                <thead>
                  <tr>
                    <th>ORDER ID</th>
                    <th>CUSTOMER</th>
                    <th>SELLER</th>
                    <th>ITEMS</th>
                    <th>TOTAL</th>
                    <th>PAYMENT</th>
                    <th>STATUS</th>
                    <th>DATE</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((order) => (
                      <tr key={order.id}>
                        <td className="ksa-order-number">{order.id}</td>

                        <td>
                          <div className="ksa-order-customer">
                            <p className="ksa-order-customer-name">{order.kmcustomer?.name}</p>
                            <p className="ksa-order-customer-email">{order.kmcustomer?.email}</p>
                          </div>
                        </td>

                        <td>{order.kmseller?.name}</td>

                        <td className="ksa-order-items">
                          {order.kmcarts?.length || 0} item(s)
                        </td>

                        <td className="ksa-order-total">
                          PKR {order.totalPrice?.toLocaleString()}
                        </td>

                        <td>{order.paymentType}</td>

                        <td>
                          <StatusBadge status={order.status} />
                        </td>

                        <td className="ksa-order-date">{formatDate(order.created_at)}</td>

                        <td>
                          <ActionDropdown actions={getActions(order)} />
                        </td>
                      </tr>
                    ))}

                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "20px" }}>
                        No orders found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          <Pagination
            totalItems={filteredOrders.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>

        {showViewModal && selectedOrder && (
          <div className="ksa-modal-overlay" onClick={() => setShowViewModal(false)}>
            <div
              className="ksa-modal ksa-modal-xl ksa-order-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-order-modal-header">
                <h3 className="ksa-order-modal-title">
                  Order ID: #{selectedOrder.id}
                </h3>
                <button className="ksa-modal-close" onClick={() => setShowViewModal(false)}>
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-order-modal-body">
                <Row className="g-4">
                  <Col xs={12} lg={7}>
                    <div className="ksa-order-section">
                      <h5 className="ksa-order-section-title">
                        <FaUser /> Customer Information
                      </h5>
                      <div className="ksa-order-customer-info">
                        <div className="ksa-order-info-item">
                          <FaUser />
                          <div>
                            <label>Name</label>
                            <p>{selectedOrder.kmcustomer?.name}</p>
                          </div>
                        </div>

                        <div className="ksa-order-info-item">
                          <FaUser />
                          <div>
                            <label>Email</label>
                            <p>{selectedOrder.kmcustomer?.email}</p>
                          </div>
                        </div>

                        <div className="ksa-order-info-item">
                          <FaPhone />
                          <div>
                            <label>Phone</label>
                            <p>{selectedOrder.kmcustomer?.phone}</p>
                          </div>
                        </div>

                        <div className="ksa-order-info-item">
                          <FaMapMarkerAlt />
                          <div>
                            <label>Shipping Address</label>
                            <p>{selectedOrder.shippingAddress}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="ksa-order-section">
                      <h5 className="ksa-order-section-title">
                        <FaStore /> Seller Information
                      </h5>
                      <div className="ksa-order-customer-info">
                        <div className="ksa-order-info-item">
                          <FaStore />
                          <div>
                            <label>Seller Name</label>
                            <p>{selectedOrder.kmseller?.name}</p>
                          </div>
                        </div>

                        <div className="ksa-order-info-item">
                          <FaUser />
                          <div>
                            <label>Seller Email</label>
                            <p>{selectedOrder.kmseller?.email}</p>
                          </div>
                        </div>

                        <div className="ksa-order-info-item">
                          <FaPhone />
                          <div>
                            <label>Seller Phone</label>
                            <p>{selectedOrder.kmseller?.phone}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="ksa-order-section">
                      <h5 className="ksa-order-section-title">
                        <FaBox /> Order Items
                      </h5>
                      <div className="ksa-order-items-list">
                        {selectedOrder.kmcarts?.map((cart, i) => (
                          <div key={i} className="ksa-order-item">
                            {cart.kmproduct?.image && (
                              <img
                                src={`${baseUrlForFiles}/${cart.kmproduct.image}`}
                                alt={cart.kmproduct?.name}
                                className="ksa-order-item-image"
                              />
                            )}

                            <div className="ksa-order-item-details">
                              <p className="ksa-order-item-name">
                                {cart.kmproduct?.name}
                              </p>

                              <p className="ksa-order-item-price">
                                PKR {cart.kmproduct?.price?.toLocaleString()} × {cart.quantity} {cart.kmproduct?.unit}
                              </p>

                              <p style={{ fontSize: "12px", color: "#888" }}>
                                {cart.kmproduct?.kmsubcategory?.kmcategory?.name}
                                {cart.kmproduct?.kmsubcategory?.name
                                  ? ` › ${cart.kmproduct.kmsubcategory.name}`
                                  : ""}
                              </p>
                            </div>

                            <p className="ksa-order-item-total">
                              PKR {(cart.kmproduct?.price * cart.quantity)?.toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="ksa-order-section">
                      <h5 className="ksa-order-section-title">
                        <FaDollarSign /> Order Summary
                      </h5>
                      <div className="ksa-order-summary">
                        <div className="ksa-order-summary-row">
                          <span>Sub Total</span>
                          <span>PKR {selectedOrder.subTotal?.toLocaleString()}</span>
                        </div>

                        <div className="ksa-order-summary-row">
                          <span>Shipping Fee</span>
                          <span>PKR 200</span>
                        </div>

                        {selectedOrder.discount > 0 && (
                          <div className="ksa-order-summary-row ksa-order-discount">
                            <span>Discount</span>
                            <span>- PKR {selectedOrder.discount?.toLocaleString()}</span>
                          </div>
                        )}

                        <div className="ksa-order-summary-row ksa-order-total-row">
                          <span>Total Price</span>
                          <span>PKR {selectedOrder.totalPrice?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {selectedOrder.paymentMethod === "bank_transfer" && (
                      <div className="ksa-order-section">
                        <h5 className="ksa-order-section-title">
                          <FaUniversity /> Bank Transfer Details
                        </h5>
                        <div className="ksa-order-info-grid">
                          <div className="ksa-order-detail-item">
                            <label>Transferred Amount</label>
                            <p>PKR {Number(selectedOrder.transferred_amount || 0).toLocaleString()}</p>
                          </div>
                          <div className="ksa-order-detail-item">
                            <label>Verification Status</label>
                            <p>{selectedOrder.paymentStatus}</p>
                          </div>
                        </div>
                        {selectedOrder.transfer_receipt_image && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              Swal.fire({
                                title: `Receipt — Order #${selectedOrder.id}`,
                                imageUrl: `${baseUrlForFiles}/${selectedOrder.transfer_receipt_image}`,
                                imageAlt: "Transfer receipt",
                                imageWidth: 400,
                                confirmButtonColor: "#097d43",
                              })
                            }
                          >
                            View Receipt
                          </Button>
                        )}
                      </div>
                    )}

                    {["Refund Pending", "Refunded"].includes(selectedOrder.paymentStatus) && (
                      <div className="ksa-order-section">
                        <h5 className="ksa-order-section-title">
                          <FaUndo /> Refund Information
                        </h5>
                        <div className="ksa-order-info-grid">
                          <div className="ksa-order-detail-item">
                            <label>Refund Status</label>
                            <p>{selectedOrder.paymentStatus}</p>
                          </div>
                          <div className="ksa-order-detail-item">
                            <label>Bank Name</label>
                            <p>{selectedOrder.refund_bank_name || "N/A"}</p>
                          </div>
                          <div className="ksa-order-detail-item">
                            <label>Account Title</label>
                            <p>{selectedOrder.refund_account_title || "N/A"}</p>
                          </div>
                          <div className="ksa-order-detail-item">
                            <label>Account Number</label>
                            <p>{selectedOrder.refund_account_number || "N/A"}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </Col>

                  <Col xs={12} lg={5}>
                    <div className="ksa-order-section">
                      <h5 className="ksa-order-section-title">Order Information</h5>
                      <div className="ksa-order-info-grid">
                        <div className="ksa-order-detail-item">
                          <label>Status</label>
                          <StatusBadge status={selectedOrder.status} />
                        </div>

                        <div className="ksa-order-detail-item">
                          <label>Payment Type</label>
                          <p>{selectedOrder.paymentType}</p>
                        </div>

                        <div className="ksa-order-detail-item">
                          <label>Order Date</label>
                          <p>{formatDate(selectedOrder.created_at)}</p>
                        </div>

                        <div className="ksa-order-detail-item">
                          <label>Last Updated</label>
                          <p>{formatDate(selectedOrder.updated_at)}</p>
                        </div>

                        <div className="ksa-order-detail-item">
                          <label>Total Items</label>
                          <p>{selectedOrder.kmcarts?.length || 0} item(s)</p>
                        </div>

                        <div className="ksa-order-detail-item">
                          <label>Customer</label>
                          <p>{selectedOrder.kmcustomer?.name}</p>
                        </div>

                        <div className="ksa-order-detail-item">
                          <label>Seller</label>
                          <p>{selectedOrder.kmseller?.name}</p>
                        </div>
                      </div>
                    </div>

                    {selectedOrder.kmdeliveryman && (
                      <div className="ksa-order-section">
                        <h5 className="ksa-order-section-title">
                          <FaTruck /> Delivery Information
                        </h5>
                        <div className="ksa-order-customer-info">
                          <div className="ksa-order-info-item">
                            <FaUser />
                            <div>
                              <label>Driver Name</label>
                              <p>{selectedOrder.kmdeliveryman?.name}</p>
                            </div>
                          </div>

                          <div className="ksa-order-info-item">
                            <FaPhone />
                            <div>
                              <label>Driver Phone</label>
                              <p>{selectedOrder.kmdeliveryman?.phone}</p>
                            </div>
                          </div>

                          <div className="ksa-order-info-item">
                            <FaMapMarkerAlt />
                            <div>
                              <label>Driver City</label>
                              <p>{selectedOrder.kmdeliveryman?.city}</p>
                            </div>
                          </div>

                          <div className="ksa-order-info-item">
                            <FaTruck />
                            <div>
                              <label>Availability</label>
                              <p>{selectedOrder.kmdeliveryman?.availability_status === "on" ? "Available" : "Unavailable"}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="ksa-order-section">
                      <h5 className="ksa-order-section-title">
                        <FaTag /> Products Summary
                      </h5>
                      <div className="ksa-order-info-grid">
                        {selectedOrder.kmcarts?.map((cart, i) => (
                          <div key={i} className="ksa-order-detail-item">
                            <label>{cart.kmproduct?.name}</label>
                            <p>
                              Qty: {cart.quantity} {cart.kmproduct?.unit}
                              &nbsp;|&nbsp;
                              PKR {(cart.kmproduct?.price * cart.quantity)?.toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                  </Col>
                </Row>
              </div>

              <div className="ksa-order-modal-footer">
                {selectedOrder.paymentMethod === "bank_transfer" && selectedOrder.paymentStatus === "Pending Verification" && (
                  <Button variant="primary" onClick={() => verifyBankTransfer(selectedOrder)}>
                    <FaUniversity size={13} style={{ marginRight: 6 }} /> Verify Payment
                  </Button>
                )}
                {selectedOrder.paymentStatus === "Refund Pending" && (
                  <Button variant="primary" onClick={() => markAsRefunded(selectedOrder)}>
                    <FaUndo size={13} style={{ marginRight: 6 }} /> Mark as Refunded
                  </Button>
                )}
                <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════
            NEW — Image-Verification Checkpoints timeline modal
        ══════════════════════════════════════════════════════ */}
        {showTimelineModal && (
          <div className="ksa-modal-overlay" onClick={() => setShowTimelineModal(false)}>
            <div
              className="ksa-modal ksa-modal-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-order-modal-header">
                <h3 className="ksa-order-modal-title">
                  <FaImages style={{ marginRight: 8 }} /> Image Timeline — Order #{timelineOrderId}
                </h3>
                <button className="ksa-modal-close" onClick={() => setShowTimelineModal(false)}>
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-order-modal-body">
                {loadingTimeline ? (
                  <p style={{ textAlign: "center", padding: "20px" }}>Loading timeline...</p>
                ) : timelineData.length === 0 ? (
                  <p style={{ textAlign: "center", padding: "20px" }}>
                    No checkpoint images have been uploaded for this order yet.
                  </p>
                ) : (
                  <Row className="g-3">
                    {timelineData.map((checkpoint, i) => (
                      <Col key={i} xs={12} sm={6} xl={3}>
                        <div className="ksa-order-section" style={{ height: "100%" }}>
                          <h5 className="ksa-order-section-title" style={{ fontSize: 14 }}>
                            {checkpoint.checkpoint}
                          </h5>
                          <p style={{ fontSize: 12.5, color: "#6c757d", margin: "0 0 8px" }}>
                            By {checkpoint.actor}
                            {checkpoint.occurred_at ? ` · ${formatDate(checkpoint.occurred_at)}` : ""}
                          </p>
                          {checkpoint.estimated_delivery_time && (
                            <p style={{ fontSize: 12.5, margin: "0 0 8px" }}>
                              Estimated Delivery: <strong>{checkpoint.estimated_delivery_time}</strong>
                            </p>
                          )}
                          <img
                            src={`${baseUrlForFiles}/${checkpoint.image}`}
                            alt={checkpoint.checkpoint}
                            style={{
                              width: "100%",
                              maxHeight: 220,
                              objectFit: "cover",
                              borderRadius: 8,
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              Swal.fire({
                                title: checkpoint.checkpoint,
                                imageUrl: `${baseUrlForFiles}/${checkpoint.image}`,
                                imageAlt: checkpoint.checkpoint,
                                imageWidth: 500,
                                confirmButtonColor: "#097d43",
                              })
                            }
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>

              <div className="ksa-order-modal-footer">
                <Button variant="secondary" onClick={() => setShowTimelineModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    </AdminLayout>
  );
};

export default OrdersList;
